import { fetchCache } from '../../../../js/utils/fetchCache.js';

export class TemplateLoader {
  static templateCache = new Map();
  static fragmentCache = new Map();
  static loadingPromises = new Map();
  static preloadingPromise = null;

  static async loadTemplate(templateName) {
    if (this.templateCache.has(templateName)) {
      return this.templateCache.get(templateName);
    }

    if (this.loadingPromises.has(templateName)) {
      return this.loadingPromises.get(templateName);
    }

    const templatePath = `/js/rankings/components/templates/${templateName}.html`;
    const loadingPromise = fetchCache.fetchText(templatePath).then((template) => {
      this.templateCache.set(templateName, template);
      this.loadingPromises.delete(templateName);
      return template;
    }).catch((error) => {
      this.loadingPromises.delete(templateName);
      console.error(`TemplateLoader: Error loading template ${templateName}:`, error);
      throw error;
    });

    this.loadingPromises.set(templateName, loadingPromise);
    return loadingPromise;
  }

  static populateTemplate(template, data) {
    let result = template;
    for (const [key, value] of Object.entries(data)) {
      const placeholder = `{${key}}`;
      const regex = new RegExp(placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
      result = result.replace(regex, value || '');
    }
    return result;
  }

  static async getPopulatedTemplate(templateName, data = {}) {
    const template = await this.loadTemplate(templateName);
    return this.populateTemplate(template, data);
  }

  static async _getParsedFragments(templateName) {
    if (this.fragmentCache.has(templateName)) {
      return this.fragmentCache.get(templateName);
    }

    const template = await this.loadTemplate(templateName);
    const doc = new DOMParser().parseFromString(template, 'text/html');
    const fragments = new Map();
    doc.querySelectorAll('[data-template]').forEach((node) => {
      fragments.set(node.getAttribute('data-template'), node);
    });
    this.fragmentCache.set(templateName, fragments);
    return fragments;
  }

  static async cloneFragment(templateName, fragmentName, data = {}) {
    const fragments = await this._getParsedFragments(templateName);
    const source = fragments.get(fragmentName);
    if (!source) {
      throw new Error(`Template fragment ${templateName}/${fragmentName} not found`);
    }

    const clone = source.cloneNode(true);
    if (Object.keys(data).length) {
      clone.innerHTML = this.populateTemplate(clone.innerHTML, data);
    }
    return clone;
  }

  static async preloadTemplates() {
    if (this.preloadingPromise) return this.preloadingPromise;

    const commonTemplates = [
      'character-modal-base',
      'character-details',
      'error-states'
    ];

    if (commonTemplates.every((template) => this.templateCache.has(template))) {
      return Promise.resolve();
    }

    try {
      this.preloadingPromise = Promise.all(
        commonTemplates.map((template) => this.loadTemplate(template))
      );
      await this.preloadingPromise;
    } catch (error) {
      console.warn('TemplateLoader: Error preloading templates:', error);
      this.preloadingPromise = null;
      throw error;
    }

    return this.preloadingPromise;
  }
}
